import { useEffect, useState } from 'react';

import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';

import { useImportCardPool } from '../../hooks';
import { formatPoints } from '../../lib/format';
import type { CardPoolOperationResult } from '../../model/types';

interface ImportPoolDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
}

/** Import a free-card pool from an Excel/CSV file (gated by `loyalty.manage`). */
export function ImportPoolDialog({ open, onClose, onDone }: ImportPoolDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CardPoolOperationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const importPool = useImportCardPool();

  useEffect(() => {
    if (open) {
      setFile(null);
      setResult(null);
      setError(null);
    }
  }, [open]);

  const submit = () => {
    if (!file) return;
    setError(null);
    importPool.mutate(file, {
      onSuccess: (res) => {
        setResult(res);
        onDone(`Импорт пула: добавлено ${formatPoints(res.added)} из ${formatPoints(res.received)}.`);
      },
      onError: (err) => setError(err.message),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Импорт пула карт</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <DialogContentText>
            Загрузите файл .xls / .xlsx / .csv со списком номеров карт. Уже существующие и некорректные
            строки будут пропущены.
          </DialogContentText>
          <Button variant="outlined" component="label" startIcon={<UploadFileRoundedIcon />}>
            {file ? file.name : 'Выбрать файл'}
            <input
              hidden
              type="file"
              accept=".xls,.xlsx,.csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {result ? (
            <Alert severity="success">
              Получено {formatPoints(result.received)}, добавлено {formatPoints(result.added)},
              дубликатов {formatPoints(result.duplicates)}, некорректных {formatPoints(result.invalid)}.
            </Alert>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={importPool.isPending}>
          {result ? 'Закрыть' : 'Отмена'}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!file || importPool.isPending}
          startIcon={importPool.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Импортировать
        </Button>
      </DialogActions>
    </Dialog>
  );
}
